import {
    FILE_SIZE_IMAGE,
    SHOP_SERVICE_NAME,
    SUPPORTED_FORMATS,
    DISPLAY_MOMENT_TIME_HM,
    DISPLAY_MOMENT_TIME_HM_AM_PM_2
} from '@/configs/constants';
import { SHOP_SERVICE_TYPE } from '@/types/enums';
import { Schedule, ShopService } from '@/types/shop';
import classNames from 'classnames';
import { TFunction } from 'i18next';
import { twMerge } from 'tailwind-merge';
import { formatDateTime } from './datetime';

export function cn(...inputs: classNames.ArgumentArray) {
    return twMerge(classNames(inputs));
}

export function getAvatarFallbackName(fullname?: string) {
    if (!fullname) return '';
    const names = fullname.trim().split(' ');

    if (names.length === 1) {
        // Trường hợp chỉ có một từ
        return `${names[0].charAt(0).toUpperCase()}${names[0].charAt(1).toUpperCase()}`;
    } else if (names.length > 1) {
        // Lấy ký tự đầu tiên của tên và họ
        const firstInitial = names[0].charAt(0).toUpperCase();
        const lastInitial = names[names.length - 1].charAt(0).toUpperCase();
        return `${firstInitial}${lastInitial}`;
    }

    return '';
}

export const checkImageSupportFormat = (files) => {
    if (!files || !(files instanceof FileList)) {
        return false;
    }
    return Array.from(files).every(
        (item: any) => SUPPORTED_FORMATS.includes(item.type) && item.size <= FILE_SIZE_IMAGE
    );
};

export const generateMinuteOptions = (step) => {
    const options: string[] = [];
    for (let i = 0; i < 60; i += step) {
        options.push(i.toString().padStart(2, '0'));
    }
    return options;
};

export const convertToHourMinuteFormat = (data: number, locale: string) => {
    const hours = Math.floor(data / 60);
    const remainingMinutes = data % 60;
    if (locale === 'vi') {
        if (hours > 0 && remainingMinutes > 0) {
            return `${hours} giờ ${remainingMinutes} phút`;
        } else if (hours > 0) {
            return `${hours} giờ`;
        } else if (remainingMinutes > 0) {
            return `${remainingMinutes} phút`;
        } else {
            return '';
        }
    } else if (locale === 'en') {
        if (hours > 0 && remainingMinutes > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`;
        } else if (hours > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''}`;
        } else if (remainingMinutes > 0) {
            return `${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`;
        } else {
            return '';
        }
    }
};

export const convertNumber = (value: number) => {
    const isNegative = value < 0;
    const absValue = Math.abs(value); // Lấy giá trị tuyệt đối để xử lý

    let result = '';

    if (absValue < 1000) {
        result = absValue.toString();
    } else if (absValue < 1000_000) {
        result = addCommas(absValue / 1000) + 'K';
    } else if (absValue < 1000_000_000) {
        result = addCommas(absValue / 1000_000) + 'M';
    } else {
        result = addCommas(absValue / 1000_000_000) + 'B';
    }

    return isNegative ? `-${result}` : result; // Thêm dấu âm nếu ban đầu là số âm
};
const addCommas = (num: number) => {
    let shortString = num.toString();
    let indexDots = shortString.indexOf('.');

    if (indexDots !== -1) {
        if (shortString[indexDots + 1] === '0') {
            shortString = shortString.slice(0, indexDots + 1).replace('.', '');
        } else {
            shortString = shortString.slice(0, indexDots + 2);
        }
    }

    let decimalNumber = shortString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return decimalNumber;
};
export const formatNumber = (num: number) => {
    const numStr = num.toString();

    return numStr?.length > 3 ? numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : numStr;
};

export const getFileExtension = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex === -1 || dotIndex === 0) {
        return '';
    }
    return fileName.slice(dotIndex + 1).toLowerCase();
};

export const getFileNameFromUrl = (url: any): string => {
    if (typeof url !== 'string' || !url) return '';
    // Loại bỏ query string nếu có
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/');
    return parts.length > 0 ? parts[parts.length - 1] : '';
};

export const generateDefaultPassword = () => {
    const characters = '0123456789';
    let randomPart = '';

    for (let i = 0; i < 6; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        randomPart += characters[randomIndex];
    }

    const password = 'M@' + randomPart;
    return password;
};

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    });
};

export const getFileInfo = (file: File) => {
    const dotIndex = file.name.lastIndexOf('.');
    if (dotIndex === -1 || dotIndex === 0) {
        return '';
    }
    return [file.name.slice(0, dotIndex).toLowerCase(), file.type.split('/')[1].toLowerCase()];
};

export const generateTimeSlots = (duration: number, openTime: string, closeTime: string): Schedule[] => {
    if (!duration || duration <= 0) return [];

    const [openHour, openMinute] = openTime.split(':').map(Number);
    const [closeHour, closeMinute] = closeTime.split(':').map(Number);

    if (isNaN(openHour) || isNaN(openMinute) || isNaN(closeHour) || isNaN(closeMinute)) {
        return [];
    }

    const openTotalMinutes = openHour * 60 + openMinute;
    const closeTotalMinutes = closeHour * 60 + closeMinute;

    if (closeTotalMinutes <= openTotalMinutes) {
        return [];
    }

    const result: Schedule[] = [];

    for (let time = openTotalMinutes; time <= closeTotalMinutes; time += duration) {
        const hours = Math.floor(time / 60);
        const minutes = time % 60;
        const time24h = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

        result.push({
            startTime:
                formatDateTime(time24h, DISPLAY_MOMENT_TIME_HM_AM_PM_2, {
                    onlyTime: true,
                    currentformat: DISPLAY_MOMENT_TIME_HM
                }) ?? '',
            status: true
        });
    }

    return result;
};

export const convertPetWeight = (weightFrom: string = '', weightTo: string = '', t?: TFunction) => {
    let result = `${weightFrom}kg`;
    if (weightTo.includes('+')) {
        result += ` - ${t ? t('words_title.up') : 'up'}`;
    } else {
        result += ` - ${weightTo}kg`;
    }
    return result;
};

export const convertServicePrice = (price?: number | string | null, t?: TFunction) => {
    if (typeof price === 'number' || typeof price === 'string') {
        return `${formatNumber(Number(price))} VND`;
    }
    return t ? t('words_title.by_consul') : '';
};

export const convertServiceList = (serviceList: ShopService[], t: TFunction) => {
    let result: any = [];
    if (serviceList.length > 0) {
        result = serviceList.map((service) => {
            let serviceName = '';
            let servicePrice: number | string = convertServicePrice(service.price, t);
            if (service.type === SHOP_SERVICE_TYPE.Dog || service.type === SHOP_SERVICE_TYPE.Cat) {
                serviceName = service.serviceName;
                serviceName += ` / ${convertPetWeight(service.petWeightFrom, service.petWeightTo, t)}`;
            } else if (service.type === SHOP_SERVICE_TYPE.Additional) {
                serviceName = `(${t('words_title.additional')}) ` + service.serviceName;
            } else {
                serviceName = service.serviceName;
            }
            return {
                serviceName,
                servicePrice
            };
        });
    }
    return result;
};

export const removeVietnameseAccents = (string) => {
    if (!string) return '';
    return string
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/Đ/g, 'D')
        .replace(/đ/g, 'd');
};

export async function uploadFileToS3UsingPresignedPost(file, presignedData) {
    const formData = new FormData();

    // Append all the fields AWS returned
    Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
    });

    // Append the actual file
    formData.append('file', file);

    // Upload to S3 using fetch
    const response = await fetch(presignedData.url, {
        method: 'POST',
        body: formData
    });

    if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
    }

    return {
        success: true,
        location: `${presignedData.url}/${presignedData.fields.key}`
    };
}

export const combineDateAndTime = (dateReview: Date | null | undefined, timeReview: string): Date | null => {
    if (!dateReview || !timeReview) return null;

    // Tách giờ và phút từ chuỗi timeReview (ví dụ: "14:30")
    const [hours, minutes] = timeReview.split(':').map(Number);

    // Tạo một bản sao của đối tượng Date và cập nhật giờ phút
    const combinedDate = new Date(dateReview);
    combinedDate.setHours(hours, minutes, 0, 0); // Đặt giờ, phút, giây, và mili giây

    return combinedDate;
};

export function extractImageSrcAndAltList(html: string): { src: string, alt: string }[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const data: { src: string, alt: string }[] = [];

    doc.querySelectorAll('img').forEach((img) => {
        const src = img.getAttribute('src');
        const alt = img.getAttribute('alt');
        data.push({
            src: src ?? '',
            alt: alt ?? ''
        });
    });

    return data;
}
export function extractTagretAndRelList(html: string): {
    href: string,
    target: string,
    rel: string,
    text: string

}[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const data: {
        href: string,
        target: string,
        rel: string,
        text: string

    }[] = [];
    doc.querySelectorAll('a').forEach((a) => {
        const href = a.getAttribute('href');
        const rel = a.getAttribute('rel');
        const target = a.getAttribute('target');
        const text = a.textContent?.trim();
        data.push({
            href: href ?? '',
            target: target ?? '',
            rel: rel ?? '',
            text: text ?? ''
        });
    });
    return data;
}
